import { useCallback, useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';

interface ResponsiveBreakpoints {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  isExtraLarge: boolean;
}

interface ChatPanelSizing {
  chatPanelWidth: number;
  minChatPanelWidth: number;
  minArtifactPanelWidth: number;
  maxChatPanelWidth: number;
}

export function useArtifactResponsive(): ResponsiveBreakpoints &
  ChatPanelSizing {
  const { width: windowWidth } = useWindowSize();

  // Responsive breakpoints
  const breakpoints = useMemo((): ResponsiveBreakpoints => {
    if (!windowWidth) {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: false,
        isLargeDesktop: false,
        isExtraLarge: false,
      };
    }

    return {
      isMobile: windowWidth < 900,
      isTablet: windowWidth >= 900 && windowWidth < 1024,
      isDesktop: windowWidth >= 1024 && windowWidth < 1440,
      isLargeDesktop: windowWidth >= 1440 && windowWidth < 1920,
      isExtraLarge: windowWidth >= 1920,
    };
  }, [windowWidth]);

  // Calculate optimal chat panel width based on screen size
  const getResponsiveChatPanelWidth = useCallback((): number => {
    if (!windowWidth) return 400; // Default fallback

    // Mobile: no chat panel (full screen artifact)
    if (windowWidth < 900) {
      return 0;
    }
    // Tablet and larger: 50/50 split for balanced reading experience
    return windowWidth * 0.5;
  }, [windowWidth]);

  // Fixed minimum widths for better UX
  const minChatPanelWidth = useMemo((): number => {
    if (!windowWidth) return 300;
    if (windowWidth < 900) return 0; // Mobile: no chat panel
    // Fixed minimum of 450px for comfortable chat reading
    return 440;
  }, [windowWidth]);

  const minArtifactPanelWidth = useMemo((): number => {
    if (!windowWidth) return 300;

    if (windowWidth < 900) return windowWidth; // Mobile: full width

    // Fixed minimum of 480px for PDF/artifact visibility
    return 480;
  }, [windowWidth]);

  const maxChatPanelWidth = useMemo((): number => {
    if (!windowWidth) return 600;
    if (windowWidth < 900) return 0; // Mobile: no chat panel
    // Allow chat panel to be up to 75% of screen width on all larger screens
    return windowWidth * 0.75;
  }, [windowWidth]);

  const chatPanelWidth = getResponsiveChatPanelWidth();

  return {
    ...breakpoints,
    chatPanelWidth,
    minChatPanelWidth,
    minArtifactPanelWidth,
    maxChatPanelWidth,
  };
}
